package com.schoolconnect.app.feature.classes

import com.schoolconnect.app.designsystem.appDateInputToApi
import com.schoolconnect.app.datasource.network.UserSessionStore
import com.schoolconnect.app.datasource.network.NetworkActivityTracker
import com.schoolconnect.app.datasource.network.request.ClassSectionUpdateRequest
import com.schoolconnect.app.datasource.network.request.ClassSectionWithSubjectsCreateRequest
import com.schoolconnect.app.datasource.network.response.ClassSectionResponse
import com.schoolconnect.app.designsystem.AppToastCenter
import com.schoolconnect.app.feature.common.DebouncedActionRunner
import com.schoolconnect.app.feature.common.AdminBootstrapSequencer
import com.schoolconnect.app.feature.common.AdminPagedListCoordinator
import com.schoolconnect.app.feature.common.offset
import com.schoolconnect.app.feature.common.resolveServerPageLoadState
import com.schoolconnect.app.feature.common.withPageIndex
import com.schoolconnect.app.feature.common.withPageSize
import com.schoolconnect.app.feature.common.ServerSort
import com.schoolconnect.app.feature.common.ServerSortOrder
import com.schoolconnect.app.repository.analytics.SchoolStatisticsRepository
import com.schoolconnect.app.repository.academicyear.AcademicYearRepository
import com.schoolconnect.app.repository.classsection.ClassSectionRepository
import com.schoolconnect.app.repository.classsubjectlink.ClassSubjectLinkRepository
import com.schoolconnect.app.repository.subject.SubjectRepository
import com.schoolconnect.app.util.scLog
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.cancel
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

class ClassesViewModel(
    schoolId: String = ""
) {
    private companion object {
        const val TAG = "[DATA_RETRIEVE_SC] ClassesViewModel"
    }

    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)
    private val reloadDebouncer = DebouncedActionRunner(scope)
    private var contextJob: Job? = null
    private var subjectOptionsJob: Job? = null
    private val bootstrapSequencer = AdminBootstrapSequencer(scope, TAG)
    private val pagedListCoordinator = AdminPagedListCoordinator(reloadDebouncer, bootstrapSequencer)

    private val normalizedSchoolId = schoolId.trim().ifBlank { UserSessionStore.getHomeRoleSchoolId().trim() }
    private val cachedStatistics = SchoolStatisticsRepository.peekSchoolStatistics(normalizedSchoolId)
    private val _uiState = MutableStateFlow(
        ClassesUiState(
            schoolId = normalizedSchoolId,
            isLoading = normalizedSchoolId.isNotBlank(),
            isAnalyticsLoading = normalizedSchoolId.isNotBlank() && cachedStatistics == null,
            analytics = cachedStatistics
        )
    )

    val uiState: StateFlow<ClassesUiState> = _uiState.asStateFlow()

    init {
        if (normalizedSchoolId.isBlank()) {
            _uiState.update {
                it.copy(
                    isLoading = false,
                    isAnalyticsLoading = false,
                    errorMessage = "School context was not available for class loading."
                )
            }
        } else {
            resolveAcademicYearContext()
        }
    }

    fun onEvent(event: ClassesUiEvent) {
        when (event) {
            ClassesUiEvent.BackClicked -> Unit
            ClassesUiEvent.RefreshRequested -> {
                queueClassesRefreshAfterAnalytics(forceRemote = true)
            }
            is ClassesUiEvent.SearchChanged -> updateFilters(
                searchQuery = event.query,
                resetPage = true
            )
            is ClassesUiEvent.SortChanged -> {
                _uiState.update {
                    val nextAscending = if (it.sortField == event.field) !it.sortAscending else true
                    it.copy(
                        sortField = event.field,
                        sortAscending = nextAscending,
                        paging = it.paging.withPageIndex(0)
                    )
                }
                pagedListCoordinator.refreshPage(forceRemote = true) { pageForceRemote ->
                    refreshClassesPage(forceRemote = pageForceRemote)
                }
            }
            is ClassesUiEvent.PageChanged -> {
                _uiState.update { it.copy(paging = it.paging.withPageIndex(event.pageIndex)) }
                pagedListCoordinator.refreshPage(forceRemote = true) { pageForceRemote ->
                    refreshClassesPage(forceRemote = pageForceRemote)
                }
            }
            is ClassesUiEvent.PageSizeChanged -> {
                _uiState.update { it.copy(paging = it.paging.withPageSize(event.pageSize)) }
                pagedListCoordinator.refreshPage(forceRemote = true) { pageForceRemote ->
                    refreshClassesPage(forceRemote = pageForceRemote)
                }
            }
            ClassesUiEvent.FilterClicked -> _uiState.update {
                it.copy(
                    dialogMode = ClassesDialogMode.FILTER,
                    filterDraft = it.filters,
                    errorMessage = "",
                    apiErrorMessage = "",
                    infoMessage = ""
                )
            }
            ClassesUiEvent.FilterDismissed -> closeDialog()
            is ClassesUiEvent.FilterApplied -> applyFilters(event.filters)
            ClassesUiEvent.FilterReset -> {
                _uiState.update {
                    it.copy(
                        filters = ClassSectionFilterCriteria(),
                        filterDraft = ClassSectionFilterCriteria(),
                        searchQuery = "",
                        dialogMode = ClassesDialogMode.NONE,
                        activeClassId = "",
                        paging = it.paging.withPageIndex(0),
                        errorMessage = "",
                        infoMessage = "Filters cleared"
                    )
                }
                pagedListCoordinator.refreshPage(forceRemote = true) { pageForceRemote ->
                    refreshClassesPage(forceRemote = pageForceRemote)
                }
            }
            ClassesUiEvent.AddClicked -> _uiState.update {
                it.copy(
                    dialogMode = ClassesDialogMode.EDITOR,
                    activeClassId = "",
                    isSaving = false,
                    editorDraft = ClassSectionEditorDraft(
                        schoolId = it.schoolId,
                        academicYearId = it.academicYearId,
                        subjectCodes = emptyList()
                    ),
                    errorMessage = "",
                    infoMessage = ""
                )
            }
            is ClassesUiEvent.ViewClicked -> openDetails(event.classId)
            is ClassesUiEvent.EditClicked -> openEditor(event.classId)
            is ClassesUiEvent.DeleteClicked -> _uiState.update {
                val row = it.classes.firstOrNull { classRow -> classRow.id == event.classId }
                if (row == null) {
                    it.copy(errorMessage = "Class not found")
                } else {
                    it.copy(
                        dialogMode = ClassesDialogMode.DELETE_CONFIRM,
                        activeClassId = row.id,
                        errorMessage = "",
                        infoMessage = ""
                    )
                }
            }
            ClassesUiEvent.DeleteConfirmed -> deleteSelected()
            ClassesUiEvent.DialogDismissed -> if (!_uiState.value.isSaving) closeDialog()
            ClassesUiEvent.EditorSaveRequested -> if (!_uiState.value.isSaving) saveEditor()
            is ClassesUiEvent.EditorClassChanged -> _uiState.update {
                it.copy(editorDraft = it.editorDraft.copy(className = event.value), errorMessage = "", apiErrorMessage = "")
            }
            is ClassesUiEvent.EditorSectionChanged -> _uiState.update {
                it.copy(editorDraft = it.editorDraft.copy(sectionName = event.value), errorMessage = "", apiErrorMessage = "")
            }
            is ClassesUiEvent.EditorSubjectCodesChanged -> _uiState.update {
                it.copy(editorDraft = it.editorDraft.copy(subjectCodes = event.values), errorMessage = "", apiErrorMessage = "")
            }
            is ClassesUiEvent.FilterClassSectionChanged -> _uiState.update {
                it.copy(filterDraft = it.filterDraft.copy(classId = event.className, sectionId = event.sectionName))
            }
            is ClassesUiEvent.FilterSubjectCodeChanged -> _uiState.update {
                it.copy(filterDraft = it.filterDraft.copy(subjectCode = event.value))
            }
            is ClassesUiEvent.FilterCreatedFromChanged -> _uiState.update {
                it.copy(filterDraft = it.filterDraft.copy(createdFrom = event.value))
            }
            is ClassesUiEvent.FilterCreatedToChanged -> _uiState.update {
                it.copy(filterDraft = it.filterDraft.copy(createdTo = event.value))
            }
            ClassesUiEvent.ClearMessages -> _uiState.update {
                it.copy(errorMessage = "", apiErrorMessage = "", infoMessage = "")
            }
        }
    }

    fun clear() {
        contextJob?.cancel()
        subjectOptionsJob?.cancel()
        pagedListCoordinator.clear()
        scope.cancel()
    }

    private fun resolveAcademicYearContext() {
        contextJob?.cancel()
        contextJob = scope.launch {
            scLog("$TAG resolveAcademicYearContext start school=$normalizedSchoolId")
            val academicYearResult = AcademicYearRepository.loadCurrentAcademicYear(normalizedSchoolId)

            academicYearResult.fold(
                onSuccess = { currentYear ->
                    if (currentYear == null) {
                        _uiState.update {
                            it.copy(
                                isLoading = false,
                                isAnalyticsLoading = false,
                                errorMessage = "Unable to resolve the current academic year."
                            )
                        }
                        return@fold
                    }

                    _uiState.update {
                        it.copy(
                            academicYearId = currentYear.id.orEmpty(),
                            academicYearLabel = currentYear.label.orEmpty(),
                            schoolId = normalizedSchoolId,
                            isLoading = true,
                            isAnalyticsLoading = true,
                            errorMessage = "",
                            infoMessage = ""
                        )
                    }

                    refreshSubjectCodeOptions(forceRemote = false)
                    queueClassesRefreshAfterAnalytics(forceRemote = false)
                    scLog(
                        "$TAG resolveAcademicYearContext success school=$normalizedSchoolId academicYearId=${currentYear.id}"
                    )
                },
                onFailure = { throwable ->
                    scLog("$TAG resolveAcademicYearContext failure school=$normalizedSchoolId", throwable)
                    _uiState.update {
                        it.copy(
                            isLoading = false,
                            isAnalyticsLoading = false,
                            errorMessage = throwable.message.orEmpty().ifBlank { "Unable to resolve academic year context." }
                        )
                    }
                }
            )
        }
    }

    private fun refreshSubjectCodeOptions(forceRemote: Boolean) {
        subjectOptionsJob?.cancel()
        subjectOptionsJob = scope.launch {
            val schoolId = _uiState.value.schoolId
            if (schoolId.isBlank()) return@launch

            scLog("$TAG refreshSubjectCodeOptions start school=$schoolId forceRemote=$forceRemote")
            val optionsResult = SubjectRepository.loadSchoolSubjectOptions(
                schoolId = schoolId,
                forceRemote = forceRemote
            )

            optionsResult.fold(
                onSuccess = { options ->
                    val classSectionOptions = ClassSectionRepository.loadSchoolClassSectionLookup(
                        schoolId = schoolId,
                        forceRemote = forceRemote
                    ).getOrDefault(emptyList())
                    _uiState.update {
                        it.copy(
                            subjectCodeOptions = options.codeById,
                            subjectSelectionOptions = options.nameByCode,
                            classSectionFilterOptions = classSectionOptions.mapNotNull { row ->
                                val className = row.className?.trim().orEmpty()
                                val sectionName = row.sectionName?.trim().orEmpty()
                                if (className.isBlank() || sectionName.isBlank()) null
                                else ClassSectionFilterOption(className, sectionName)
                            }.distinct()
                        )
                    }
                    scLog(
                        "$TAG refreshSubjectCodeOptions success school=$schoolId codes=${options.codeById.size} selections=${options.nameByCode.size}"
                    )
                },
                onFailure = { throwable ->
                    if (throwable is CancellationException) {
                        scLog("$TAG refreshSubjectCodeOptions cancelled school=$schoolId")
                    } else {
                        scLog("$TAG refreshSubjectCodeOptions failure school=$schoolId", throwable)
                    }
                }
            )
        }
    }

    private fun updateFilters(
        searchQuery: String = _uiState.value.searchQuery,
        resetPage: Boolean
    ) {
        _uiState.update {
            it.copy(
                searchQuery = searchQuery,
                paging = if (resetPage) it.paging.withPageIndex(0) else it.paging,
                errorMessage = "",
                infoMessage = ""
            )
        }
        scheduleClassesReload()
    }

    private fun applyFilters(filters: ClassSectionFilterCriteria) {
        _uiState.update {
            it.copy(
                filters = filters,
                filterDraft = filters,
                dialogMode = ClassesDialogMode.NONE,
                activeClassId = "",
                paging = it.paging.withPageIndex(0),
                errorMessage = "",
                infoMessage = ""
            )
        }
        pagedListCoordinator.refreshPage(forceRemote = true) { pageForceRemote ->
            refreshClassesPage(forceRemote = pageForceRemote)
        }
    }

    private fun closeDialog() {
        _uiState.update {
            it.copy(
                dialogMode = ClassesDialogMode.NONE,
                activeClassId = "",
                isSaving = false,
                isLoading = false,
                errorMessage = "",
                apiErrorMessage = "",
                infoMessage = ""
            )
        }
    }

    private fun scheduleClassesReload(immediate: Boolean = false) {
        pagedListCoordinator.schedulePage(immediate = immediate) { pageForceRemote ->
            refreshClassesPage(forceRemote = pageForceRemote)
        }
    }

    private fun queueClassesRefreshAfterAnalytics(forceRemote: Boolean) {
        _uiState.update {
            it.copy(
                isLoading = true,
                isAnalyticsLoading = true,
                errorMessage = "",
                infoMessage = ""
            )
        }
        pagedListCoordinator.refresh(
            forceRemote = forceRemote,
            loadAnalytics = { refreshClassesAnalytics(forceRemote = forceRemote) },
            loadPage = { pageForceRemote -> refreshClassesPage(forceRemote = pageForceRemote) }
        )
    }

    private fun refreshClassesAfterMutation() {
        pagedListCoordinator.refresh(
            forceRemote = true,
            loadAnalytics = { refreshClassesAnalytics(forceRemote = true) },
            loadPage = { pageForceRemote -> refreshClassesPage(forceRemote = pageForceRemote) }
        )
    }

    private suspend fun refreshClassesAnalytics(forceRemote: Boolean) {
        val currentState = _uiState.value
        if (currentState.schoolId.isBlank() || currentState.academicYearId.isBlank()) return

        scLog(
            "$TAG refreshClassesAnalytics start school=${currentState.schoolId} academicYear=${currentState.academicYearId} forceRemote=$forceRemote"
        )
        _uiState.update { it.copy(isAnalyticsLoading = true) }

        val result = SchoolStatisticsRepository.loadSchoolStatistics(
            schoolId = currentState.schoolId,
            forceRemote = forceRemote
        )

        result.fold(
            onSuccess = { analytics ->
                _uiState.update {
                    it.copy(
                        analytics = analytics,
                        summary = ClassesSummaryUi(
                            total = analytics.statistics.classes.total,
                            active = analytics.statistics.classes.active,
                            inactive = analytics.statistics.classes.inactive
                        ),
                        isAnalyticsLoading = false,
                        errorMessage = "",
                        infoMessage = ""
                    )
                }
                scLog(
                    "$TAG refreshClassesAnalytics success school=${currentState.schoolId} academicYear=${currentState.academicYearId} total=${analytics.statistics.classes.total} active=${analytics.statistics.classes.active} inactive=${analytics.statistics.classes.inactive}"
                )
            },
            onFailure = { throwable ->
                if (throwable is CancellationException) {
                    scLog(
                        "$TAG refreshClassesAnalytics cancelled school=${currentState.schoolId} academicYear=${currentState.academicYearId}"
                    )
                } else {
                    scLog(
                        "$TAG refreshClassesAnalytics failure school=${currentState.schoolId} academicYear=${currentState.academicYearId}",
                        throwable
                    )
                    _uiState.update {
                        it.copy(
                            isAnalyticsLoading = false,
                            errorMessage = throwable.message.orEmpty().ifBlank { "Unable to load class analytics." }
                        )
                    }
                }
            }
        )
    }

    private suspend fun refreshClassesPage(forceRemote: Boolean) =
        NetworkActivityTracker.withoutBlockingLoader {
            refreshClassesPageInternal(forceRemote)
        }

    private suspend fun refreshClassesPageInternal(forceRemote: Boolean) {
        val currentState = _uiState.value
        val paging = currentState.paging
        if (currentState.schoolId.isBlank() || currentState.academicYearId.isBlank()) return

        val query = currentState.searchQuery.trim()
        val hasFilters = query.isNotBlank() ||
            currentState.filters.classId.isNotBlank() ||
            currentState.filters.sectionId.isNotBlank() ||
            currentState.filters.subjectCode.isNotBlank() ||
            currentState.filters.createdFrom.isNotBlank() ||
            currentState.filters.createdTo.isNotBlank()

        scLog(
            buildString {
                append("$TAG refreshClassesPage start ")
                append("school=${currentState.schoolId} ")
                append("academicYear=${currentState.academicYearId} ")
                append("q=${query.ifBlank { "<blank>" }} ")
                append("classId=${currentState.filters.classId.ifBlank { "<blank>" }} ")
                append("sectionId=${currentState.filters.sectionId.ifBlank { "<blank>" }} ")
                append("pageIndex=${paging.pageIndex} ")
                append("pageSize=${paging.pageSize} ")
                append("offset=${paging.offset()} ")
                append("forceRemote=$forceRemote")
            }
        )
        _uiState.update {
            it.copy(
                isLoading = true,
                classes = emptyList(),
                errorMessage = "",
                infoMessage = ""
            )
        }

        val result = ClassSectionRepository.loadSchoolClassSectionsPage(
            schoolId = currentState.schoolId,
            academicYearId = currentState.academicYearId,
            query = query,
            classId = currentState.filters.classId,
            sectionId = currentState.filters.sectionId,
            subjectCode = currentState.filters.subjectCode,
            status = "",
            createdFrom = appDateInputToApi(currentState.filters.createdFrom).orEmpty(),
            createdTo = appDateInputToApi(currentState.filters.createdTo).orEmpty(),
            pageIndex = paging.pageIndex,
            pageSize = paging.pageSize,
            sort = ServerSort(
                field = currentState.sortField.apiField,
                order = if (currentState.sortAscending) ServerSortOrder.ASC else ServerSortOrder.DESC
            ),
            forceRemote = forceRemote
        )

        result.fold(
            onSuccess = { pageResult ->
                val mappedRows = pageResult.items
                    .distinctBy { it.id }
                    .map { it.toRowUi() }

                val pageLoad = resolveServerPageLoadState(
                    exactTotal = pageResult.totalCount,
                    paging = paging,
                    currentItemCount = mappedRows.size,
                    pageOffset = paging.offset()
                )
                if (pageLoad.clampedPageIndex != pageLoad.paging.pageIndex) {
                    _uiState.update { current ->
                        current.copy(
                            paging = current.paging.withPageIndex(pageLoad.clampedPageIndex),
                            isLoading = false
                        )
                    }
                    refreshClassesPage(forceRemote = forceRemote)
                    return@fold
                }

                _uiState.update { current ->
                    current.copy(
                        isLoading = false,
                        classes = mappedRows,
                        summary = if (current.analytics == null) classesSummary(mappedRows) else current.summary,
                        paging = pageLoad.paging,
                        errorMessage = "",
                        infoMessage = current.infoMessage
                    )
                }
                scLog(
                    "$TAG refreshClassesPage success school=${currentState.schoolId} academicYear=${currentState.academicYearId} count=${mappedRows.size} totalCount=${pageLoad.paging.totalCount} exact=${pageLoad.paging.isTotalCountExact}"
                )
            },
            onFailure = { throwable ->
                if (throwable is CancellationException) {
                    scLog(
                        "$TAG refreshClassesPage cancelled school=${currentState.schoolId} academicYear=${currentState.academicYearId}"
                    )
                } else {
                    scLog(
                        "$TAG refreshClassesPage failure school=${currentState.schoolId} academicYear=${currentState.academicYearId}",
                        throwable
                    )
                    _uiState.update {
                        it.copy(
                            isLoading = false,
                            errorMessage = throwable.message.orEmpty().ifBlank { "Unable to load classes." }
                        )
                    }
                }
            }
        )
    }

    private fun deleteSelected() {
        val classId = _uiState.value.activeClassId
        val row = _uiState.value.classes.firstOrNull { it.id == classId } ?: run {
            _uiState.update {
                it.copy(errorMessage = "Class not found.", infoMessage = "")
            }
            return
        }

        _uiState.update {
            it.copy(
                isSaving = true,
                isLoading = true,
                errorMessage = "",
                infoMessage = ""
            )
        }

        scope.launch {
            try {
                ClassSectionRepository.deleteClassSection(row.id).getOrThrow()
                refreshClassesAfterMutation()
                _uiState.update {
                    it.copy(
                        isLoading = false,
                        isSaving = false,
                        dialogMode = ClassesDialogMode.NONE,
                        activeClassId = "",
                        infoMessage = "Deleted ${row.className}-${row.sectionName}"
                    )
                }
                scLog(
                    "$TAG deleteClassSection success school=${_uiState.value.schoolId} academicYear=${_uiState.value.academicYearId} classId=${row.id}"
                )
            } catch (throwable: Throwable) {
                if (throwable is CancellationException) {
                    scLog(
                        "$TAG deleteClassSection cancelled school=${_uiState.value.schoolId} academicYear=${_uiState.value.academicYearId} classId=${row.id}"
                    )
                } else {
                    scLog(
                        "$TAG deleteClassSection failure school=${_uiState.value.schoolId} academicYear=${_uiState.value.academicYearId} classId=${row.id}",
                        throwable
                    )
                    _uiState.update {
                        it.copy(
                            isLoading = false,
                            isSaving = false,
                            errorMessage = throwable.message.orEmpty().ifBlank { "Failed to delete class." }
                        )
                    }
                }
            }
        }
    }

    private fun openDetails(classId: String) {
        _uiState.update { state ->
            val row = state.classes.firstOrNull { it.id == classId }
            if (row == null) {
                state.copy(errorMessage = "Class not found")
            } else {
                state.copy(
                    dialogMode = ClassesDialogMode.DETAILS,
                    activeClassId = row.id,
                    errorMessage = "",
                    infoMessage = ""
                )
            }
        }
    }

    private fun openEditor(classId: String) {
        _uiState.update { state ->
            val row = state.classes.firstOrNull { it.id == classId }
            val draft = if (row == null) {
                ClassSectionEditorDraft(
                    schoolId = state.schoolId,
                    academicYearId = state.academicYearId,
                    subjectCodes = emptyList()
                )
            } else {
                row.toEditorDraft()
            }
            state.copy(
                dialogMode = ClassesDialogMode.EDITOR,
                activeClassId = row?.id.orEmpty(),
                editorDraft = draft,
                isSaving = false,
                errorMessage = "",
                apiErrorMessage = "",
                infoMessage = ""
            )
        }
    }

    private fun saveEditor() {
        val currentState = _uiState.value
        val draft = currentState.editorDraft
        _uiState.update { it.copy(apiErrorMessage = "") }
        val validation = validateClassSectionEditor(draft)
        if (validation.hasAnyError) {
            _uiState.update {
                it.copy(
                    errorMessage = listOf(
                        validation.className,
                        validation.sectionName,
                        validation.subjectCodes
                    ).filter(String::isNotBlank).joinToString(" "),
                    infoMessage = ""
                )
            }
            return
        }

        _uiState.update {
            it.copy(
                isSaving = true,
                isLoading = true,
                errorMessage = "",
                infoMessage = ""
            )
        }

        scope.launch {
            try {
                val subjectIds = resolveSubjectIds(
                    schoolId = currentState.schoolId,
                    subjectCodes = draft.subjectCodes,
                    forceRemote = true
                )
                val savedSection = if (draft.id.isBlank()) {
                    ClassSectionRepository.createClassSectionWithSubjects(
                        schoolId = currentState.schoolId,
                        request = ClassSectionWithSubjectsCreateRequest(
                            academicYearId = currentState.academicYearId,
                            className = draft.className.trim(),
                            sectionName = draft.sectionName.trim(),
                            isActive = true,
                            subjectIds = subjectIds
                        )
                    ).getOrThrow()
                } else {
                    ClassSectionRepository.updateClassSection(
                        classSectionId = draft.id,
                        request = ClassSectionUpdateRequest(
                            className = draft.className.trim(),
                            sectionName = draft.sectionName.trim(),
                            isActive = null
                        )
                    ).getOrThrow()
                }

                if (draft.id.isNotBlank()) {
                    syncClassSubjectLinks(
                        schoolId = currentState.schoolId,
                        classSectionId = savedSection.id.orEmpty(),
                        subjectIds = subjectIds
                    )
                }

                ClassSectionRepository.invalidateCaches()
                AppToastCenter.showSuccess(
                    if (draft.id.isBlank()) {
                        "Added ${draft.className.trim()}-${draft.sectionName.trim()}"
                    } else {
                        "Updated ${draft.className.trim()}-${draft.sectionName.trim()}"
                    }
                )
                _uiState.update {
                    it.copy(
                        isLoading = false,
                        isSaving = false,
                        dialogMode = ClassesDialogMode.NONE,
                        activeClassId = "",
                        editorDraft = ClassSectionEditorDraft(
                            schoolId = currentState.schoolId,
                            academicYearId = currentState.academicYearId,
                    subjectCodes = emptyList()
                        ),
                        errorMessage = "",
                        apiErrorMessage = "",
                        infoMessage = ""
                    )
                }
                refreshClassesAfterMutation()
                scLog(
                    "$TAG saveEditor success school=${currentState.schoolId} academicYear=${currentState.academicYearId} classId=${draft.id.ifBlank { "<new>" }}"
                )
            } catch (throwable: Throwable) {
                if (throwable is CancellationException) {
                    scLog(
                        "$TAG saveEditor cancelled school=${currentState.schoolId} academicYear=${currentState.academicYearId} classId=${draft.id.ifBlank { "<new>" }}"
                    )
                } else {
                    scLog(
                        "$TAG saveEditor failure school=${currentState.schoolId} academicYear=${currentState.academicYearId} classId=${draft.id.ifBlank { "<new>" }}",
                        throwable
                    )
                    _uiState.update {
                        it.copy(
                            isLoading = false,
                            isSaving = false,
                            apiErrorMessage = throwable.message.orEmpty().ifBlank { "Failed to save class." }
                        )
                    }
                }
            }
        }
    }

    private fun ClassSectionResponse.toRowUi(): ClassSectionRowUi = ClassSectionRowUi(
        id = id.orEmpty(),
        schoolId = schoolId.orEmpty(),
        academicYearId = academicYearId.orEmpty(),
        className = className.orEmpty(),
        sectionName = sectionName.orEmpty(),
        subjectCodes = subjectCodes.orEmpty().joinToString(", "),
        subjectCount = subjectCodes.orEmpty().size,
        status = classSectionStatusFromBoolean(isActive == true),
        createdAt = createdAt.orEmpty().toDisplayDate(),
        updatedAt = updatedAt.orEmpty().toDisplayDate(),
        createdBy = createdByName.orEmpty().ifBlank { createdById.orEmpty() },
        updatedBy = updatedByName.orEmpty().ifBlank { updatedById.orEmpty() }
    )

    private suspend fun syncClassSubjectLinks(
        schoolId: String,
        classSectionId: String,
        subjectIds: List<String>
    ) {
        val targetSubjectIds = subjectIds.distinct()
        val existingLinks = ClassSubjectLinkRepository.loadClassSectionSubjectLinks(
            schoolId = schoolId,
            classSectionId = classSectionId,
            forceRemote = true
        ).getOrThrow()
        val existingSubjectIds = existingLinks.mapNotNull { it.subjectId }.toSet()

        existingLinks.filter { it.subjectId?.let { subjectId -> subjectId !in targetSubjectIds } == true }.forEach { link ->
            ClassSubjectLinkRepository.deleteClassSubjectLink(link.id.orEmpty()).getOrThrow()
        }
        targetSubjectIds.filterNot { it in existingSubjectIds }.forEach { subjectId ->
            ClassSubjectLinkRepository.createClassSubjectLink(
                com.schoolconnect.app.datasource.network.request.ClassSubjectLinkCreateRequest(
                    classSectionId = classSectionId,
                    subjectId = subjectId,
                    isActive = true
                )
            ).getOrThrow()
        }
        ClassSubjectLinkRepository.invalidateCaches()
    }

    private suspend fun resolveSubjectIds(
        schoolId: String,
        subjectCodes: List<String>,
        forceRemote: Boolean
    ): List<String> {
        val normalizedCodes = subjectCodes.map { it.trim() }.filter { it.isNotBlank() }.distinct()
        if (normalizedCodes.isEmpty()) return emptyList()
        val subjectCodeMap = _uiState.value.subjectCodeOptions.ifEmpty {
            SubjectRepository.loadSchoolSubjectShortCodeMap(
                schoolId = schoolId,
                forceRemote = forceRemote
            ).getOrThrow()
        }
        val subjectIdByCode = subjectCodeMap.entries.associate { it.value.uppercase() to it.key }
        val targetSubjectIds = normalizedCodes.mapNotNull { code -> subjectIdByCode[code.uppercase()] }.distinct()
        if (targetSubjectIds.size != normalizedCodes.size) {
            error("Some subject codes could not be resolved for the selected school")
        }
        return targetSubjectIds
    }

    private fun String.toDisplayDate(): String {
        val trimmed = trim()
        if (trimmed.isBlank()) return ""
        val datePart = trimmed.substringBefore("T").takeIf { it.length >= 10 }?.take(10) ?: return trimmed
        val parts = datePart.split("-")
        if (parts.size != 3) return trimmed
        return "${parts[2]}-${parts[1]}-${parts[0]}"
    }
}
